import firebase from '../firebase';

// How stale the stored lastActiveAt must be before we write again. The dashboard
// can mount many times a day; we only want a coarse "last active" signal, so a
// single conditional write per ~24h is plenty and keeps this cheap.
const LAST_ACTIVE_THROTTLE_MS = 24 * 60 * 60 * 1000;

// Normalize a stored lastActiveAt value (Firestore Timestamp, {seconds}, Date,
// number, or string) to epoch milliseconds, or null when absent/unparseable.
function toMillis(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v === 'object' && v.seconds !== undefined) return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return isNaN(t) ? null : t;
}

/**
 * Throttled "last active = opened the dashboard" write on the user doc.
 *
 * Writes user/{uid}.lastActiveAt = serverTimestamp() ONLY when the stored value
 * is missing or older than ~24h, so it is not a write on every navigation. The
 * write is additive (set with merge) and never touches any other field. The
 * nightly rollup reads this value (else falls back to Auth lastSignInTime).
 *
 * @param {string} uid - the signed-in user's uid
 * @param {object} [knownUserData] - the already-loaded user doc data, if the
 *   caller has it in hand (UserDashboard does); avoids an extra read. When
 *   omitted (Aero), one get is performed to read the current lastActiveAt.
 */
export async function touchLastActive(uid, knownUserData) {
  if (!uid) return;
  try {
    const db = firebase.firestore();
    let lastMs;
    if (knownUserData !== undefined) {
      lastMs = toMillis(knownUserData && knownUserData.lastActiveAt);
    } else {
      const snap = await db.collection('user').doc(uid).get();
      lastMs = snap.exists ? toMillis(snap.data().lastActiveAt) : null;
    }
    if (lastMs !== null && Date.now() - lastMs < LAST_ACTIVE_THROTTLE_MS) return;
    await db.collection('user').doc(uid).set(
      { lastActiveAt: firebase.firestore.FieldValue.serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    // Activity tracking is best-effort; never disrupt the dashboard on failure.
    console.error('touchLastActive failed:', err);
  }
}
